/**
 * AtlasWaterModule — the Commons sea (terrain + water realism wave).
 *
 * Continental basins below `commonsSeaLevel(seed)` fill with the engine's
 * realistic water: a FIXED-footprint plane (the engine's shoreline features
 * need one) that FOLLOWS the player in half-footprint steps. Each recenter
 * re-bakes the engine ShorelineField from `stampedHeight` — same texture
 * object, new texels, no shader rebuild — so waves SHOAL onto real beaches,
 * surf foam hugs the coast, and shallow water tints sand. The wet-sand band
 * on the terrain side lives in the ground shader (wetness.applyBiomeGround).
 *
 * Commons-only: personal worlds get the new terrain, not water. Rendering
 * only — the server does not gate movement/placement against water yet.
 * Sun direction tracks the sky (uSunDir); an absorption-tinted FogExp2
 * swaps in while the camera is submerged (AtmosphereModule's fog object is
 * saved and restored by reference).
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import {
  createRealisticWaterMaterial,
  DEFAULT_REALISTIC_WATER,
  type RealisticWaterHandles,
} from 'enginex/environment/water/RealisticWaterMaterial.js';
import { createWaveSet, sampleHeight, sampleNormal, type GerstnerWave, type WaveModulation } from 'enginex/environment/water/GerstnerWaves.js';
import { cos, sin, uniform } from 'three/tsl';
import { GpuParticleEmitter } from 'enginex/particles/gpu/GpuParticleEmitter.js';
import { CpuParticleAdapter } from 'enginex/particles/gpu/CpuParticleAdapter.js';
import { applySoftParticles } from 'enginex/vfx/SoftParticleModifier.js';
import { ShorelineField } from 'enginex/environment/water/ShorelineField.js';
import { UnderwaterOverlay } from 'enginex/environment/water/UnderwaterOverlay.js';
import { generateWaterNormalMap } from 'enginex/environment/WaterSystem.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { ensureWaterField, waterFx, type WaterSurfaceProvider } from '../lib/water_fx.js';
import {
  seaStateFor, windHeadingRotation, slew, slewAngle, AMP_SLEW_PER_S, HEADING_SLEW_RAD_PER_S,
  rainDropsThisTick, RAIN_DROP_STAMP, type SeaState,
} from '../lib/water_physics.js';
import type { WeatherModule } from './WeatherModule.js';
import { FoamAccumulator } from 'enginex/environment/water/FoamAccumulator.js';
import { CausticsProjector } from 'enginex/environment/water/CausticsProjector.js';
import { stage3Stats } from '../lib/stage3_fx.js';
import { createUnderwaterShafts } from 'enginex/environment/water/UnderwaterShafts.js';
import { stage4Stats } from '../lib/stage4_fx.js';
import { sfx } from '../lib/sfx.js';
import { atlas } from '../state.js';
import { postRiverTable } from '../lib/hydrology_load.js';
import { bus } from '../bus.js';
import { meshHeight } from '../lib/terrain_stamp.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { riverWaterAt } from '../../../shared/terrain.js';
import { setUnderwaterFog } from './AtmosphereModule.js';
import { createLakeBake, insertLakeRegion, lakeSurfaceAtBaked, nearestLakeLevel, LAKE_GRID_N, LAKE_REGION_SIZE, type LakeBake, type LakeRegion } from '../lib/lakes.js';
import type { WaterBakeJob, WaterBakeResult } from '../workers/water_bake_worker.js';
import { uSunDir } from './SkyModule.js';
import { waterIlluminance, waterSkyTint } from '../lib/water_lighting.js';

/** Water plane footprint (m). Beyond it the sea fades below the horizon fog.
 *  Doubled (unified-render wave): the sea reaches ~1.5 km half-extent to better
 *  match the extended terrain/city draw distance. SEGMENTS + SHORE_RES scale with
 *  it so the 8 m vertex spacing + shoreline texel density (the anti-checkerboard
 *  Nyquist contract) are preserved exactly. */
const FOOTPRINT = 3072;
/**
 * Displacement grid segments. 192 -> 8 m vertex spacing over the footprint.
 * WHY: the old 96 (16 m spacing) put EVERY wave of the 22 m-cascade sea
 * below the grid's 32 m Nyquist limit — each vertex sampled the waves at a
 * pseudo-random phase, every quad tilted independently, and the sea read as
 * a checkerboard of flat tiles (user screenshot 2026-08-23). 8 m spacing
 * samples the 22 m swell cleanly; everything shorter is excluded from the
 * vertex stage via vertexWavelengthCutoff and lives in the per-pixel
 * analytic normal instead (which cannot alias on the mesh). Net vertex cost
 * is ~flat: 4x the vertices, but 1 displacement wave in the shader not 6.
 */
const SEGMENTS = 384;
/**
 * Shortest wavelength the sea GEOMETRY displaces (2.5x the 8 m vertex
 * spacing). Waves below it render through the fragment normal/foam only.
 */
const VERTEX_WAVE_CUTOFF = 2.5 * (FOOTPRINT / SEGMENTS);
/** Recenter when the camera strays this far from the bake center. */
const REBAKE_DIST = FOOTPRINT / 4;
/** Recenter snap step — keeps rebakes deterministic per grid cell. */
const SNAP = 64;
/** ShorelineField texels per side (8 m/texel over the footprint). */
const SHORE_RES = 320;

export class AtlasWaterModule implements IModule {
  readonly name = 'AtlasWater';

  private app: App | null = null;
  private scene!: THREE.Scene;
  private mesh: THREE.Mesh | null = null;
  /** Boot loading screen: the Commons sea surface is built. */
  get ready(): boolean { return this.mesh !== null; }
  private handles: RealisticWaterHandles | null = null;
  /** Stage 3 (2026-09-09): persistent whitecap trails (CPU foam grid the sea samples) + projected caustics (a small ortho render per frame). Built with the material — the menu rows apply at the next world entry. */
  private foam: FoamAccumulator | null = null;
  private caustics: CausticsProjector | null = null;
  private causticFrame = 0;
  /** Stage 4: light shafts around the submerged camera (built with the overlay, visible only under). */
  private shafts: { mesh: THREE.Mesh; uniforms: { intensity: { value: number }; depth: { value: number }; slant: { value: THREE.Vector2 } } } | null = null;
  /** The last inland/wet probe (per snapped centre) — requestRecenter re-enters every tick while a bake is pending. */
  private lastProbe: { x: number; z: number; wet: boolean } | null = null;
  /** Scratch colors for the per-frame sky-side tint ramp (no allocations). */
  private readonly _skyTint = new THREE.Color();
  private shoreline: ShorelineField | null = null;
  private normalMap: THREE.DataTexture | null = null;
  private seed = '';
  private seaLevel = 0;
  private centerX = 0;
  private centerZ = 0;
  private time = 0;
  private readonly camPos = new THREE.Vector3();
  // ── Sliced shore bake: bed heights fill a scratch grid a few rows per
  // frame using meshHeight — the RENDERED truth (far chunks interpolate at
  // ~10.7 m spacing, so the analytic field drifts meters off the visible
  // ground on steep shores and the water read as floating). One fast engine
  // rebake then reads the grid. Keeps travel-time recenters hitch-free.
  private readonly scratch = new Float32Array(SHORE_RES * SHORE_RES);
  private bakeTarget: { x: number; z: number } | null = null;
  private bakeRow = 0;
  // ── Hydrology lakes: a second, region-following water layer. One mesh +
  // one material live for the session; per region only the geometry and the
  // depth texture's texels change (no shader rebuilds).
  private lakeMesh: THREE.Mesh | null = null;
  private lakeHandles: RealisticWaterHandles | null = null;
  /** The region the lake mesh currently shows (its mirror plane follows the nearest lake). */
  private lakeRegion: LakeRegion | null = null;
  private mirrorTick = 0;
  private readonly _mirrorN = new THREE.Vector3();
  private readonly _mirrorP = new THREE.Vector3();
  private lakeDepthTex: THREE.DataTexture | null = null;
  private readonly lakeBytes = new Uint8Array(LAKE_GRID_N * LAKE_GRID_N);
  private lakeBake: LakeBake | null = null;
  private lakeBakeRow = 0;
  private lakeCenterX = Infinity; // Infinity forces the initial bake
  private lakeCenterZ = Infinity;
  // ── Perf wave 2: bakes run in a Web Worker (terrain_chunk_worker pattern).
  // The sliced main-thread paths remain as the no-Worker fallback.
  private bakeWorker: Worker | null = null;
  private workerBroken = false;
  private jobId = 0;
  private pendingShoreId = 0;
  private pendingLakeId = 0;
  private bakeBeatMs = 0; // last dispatch OR reply — the stall watchdog's heartbeat
  /** true after the worker's FIRST reply — before that it may still be importing its module graph (cold dev server: tens of seconds) */
  private bakeEverReplied = false;
  /** The shore job in flight (its target — the watchdog's sliced redo bakes THERE, not at the stale centre). */
  private pendingShoreAt: { x: number; z: number } | null = null;
  private shoreJobsPosted = 0;
  private shoreRepliesDropped = 0;
  private static readonly LAKE_ENCODE_MAX = 8;
  private submerged = false;
  /** Submerged-camera screen effect (engine UnderwaterOverlay; eager + pre-warmed). */
  private overlay: UnderwaterOverlay | null = null;
  // ── Interactive water (2026-09-11): the wave sets the CPU height query
  // shares with the shaders, the wind's sea state (slewed), the interaction
  // field's rain debt and the splash emitter.
  private waves: GerstnerWave[] = [];
  private lakeWaves: GerstnerWave[] = [];
  /** The live modulation the sea material's uniforms carry (mutated in place; the foam grid reads it). */
  private readonly mod: WaveModulation = { ampScale: 1, dirRot: 0 };
  /** Lakes are sheltered: half the sea's amplitude response, the same heading. */
  private readonly lakeMod: WaveModulation = { ampScale: 1, dirRot: 0 };
  private seaState: SeaState = seaStateFor(0.45);
  private readonly rainDebt = { debt: 0 };
  private weather: WeatherModule | null = null;
  private splashEmitter: GpuParticleEmitter | CpuParticleAdapter | null = null;
  private readonly _splashPos = new THREE.Vector3();
  private readonly _windDrift = new THREE.Vector2(1, 0.8);
  private fxStats = { rainThisSecond: 0, stampsSeen: 0, seaAmp: 1, seaRot: 0, foamJ: 0.6, fieldOrigin: [0, 0] as [number, number] };

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    bus.on('atlas:world-entered', () => this.refresh());
    bus.on('atlas:environment-updated', () => this.refresh());
    app.world.addSystem(makeSystem('AtlasWater', Phase.RenderSub, 36, (dt) => this.tick(dt)));
    // Probe diagnostics: submerge state + a deep-texel finder (world coords)
    // so a headless harness can teleport into real water deterministically.
    (globalThis as unknown as { __water?: () => object }).__water = () => ({
      lakeAt: (x: number, z: number): number | null => lakeSurfaceAtBaked(x, z), // the baked lake surface under a world point (probes)
      seaLevel: this.seaLevel,
      submerged: this.submerged,
      seaVisible: this.mesh?.visible ?? false,
      camY: Math.round(this.camPos.y * 100) / 100,
      depthHere: this.shoreline
        ? Math.round(this.shoreline.depthAt(this.camPos.x - this.centerX, this.camPos.z - this.centerZ) * 100) / 100
        : -1,
      center: [this.centerX, this.centerZ],
      maxDepth: this.shoreline ? Math.round(this.shoreline.maxDepth * 100) / 100 : -1,
      overlayVisible: this.overlay?.mesh.visible ?? false,
      lakeVisible: this.lakeMesh?.visible ?? false,
      lakeSurfaceHere: lakeSurfaceAtBaked(this.camPos.x, this.camPos.z),
      reflection: this.reflectionState(),
      fluid: this.fluidState(),
      shore: {
        pending: this.pendingShoreId !== 0,
        jobsPosted: this.shoreJobsPosted,
        repliesDropped: this.shoreRepliesDropped,
        sliced: this.bakeTarget !== null,
        workerBroken: this.workerBroken,
        everReplied: this.bakeEverReplied,
      },
    });
    // Coast scanner: pure terrain math (no bake needed) — the nearest point
    // with a sea column ≥ `min` m within `radius` m of (x0, z0).
    (globalThis as unknown as { __waterScanCoast?: (x0: number, z0: number, radius?: number, min?: number) => number[] | null }).__waterScanCoast =
      (x0, z0, radius = 24_000, min = 4) => {
        if (!this.seed) return null;
        let best: number[] | null = null;
        let bestD = Infinity;
        for (let r = 1000; r <= radius; r += 1000) {
          for (let a = 0; a < 24; a++) {
            const ang = (a / 24) * Math.PI * 2;
            const x = x0 + Math.cos(ang) * r;
            const z = z0 + Math.sin(ang) * r;
            const depth = this.seaLevel - meshHeight(this.seed, x, z);
            if (depth >= min && r < bestD) { bestD = r; best = [x, z, Math.round(depth * 10) / 10]; }
          }
          if (best) break;
        }
        return best;
      };
    // Lake finder: deepest baked lake texel near the player (world coords).
    (globalThis as unknown as { __waterFindLake?: (min?: number) => number[] | null }).__waterFindLake = (min = 1.5) => {
      const n = LAKE_GRID_N;
      let best: number[] | null = null;
      let bestDepth = min;
      for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
          const d = this.lakeBytes[row * n + col] / 255 * AtlasWaterModule.LAKE_ENCODE_MAX;
          if (d > bestDepth) {
            bestDepth = d;
            best = [
              this.lakeCenterX + (col / (n - 1) - 0.5) * LAKE_REGION_SIZE,
              this.lakeCenterZ + (row / (n - 1) - 0.5) * LAKE_REGION_SIZE,
              Math.round(d * 10) / 10,
            ];
          }
        }
      }
      return best;
    };
    (globalThis as unknown as { __waterFindDeep?: (min?: number) => number[] | null }).__waterFindDeep = (min = 3) => {
      const n = SHORE_RES;
      let best: number[] | null = null;
      let bestDepth = min;
      for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
          const depth = this.seaLevel - this.scratch[row * n + col];
          if (depth > bestDepth) {
            bestDepth = depth;
            best = [
              this.centerX + (col / (n - 1) - 0.5) * FOOTPRINT,
              this.centerZ + (row / (n - 1) - 0.5) * FOOTPRINT,
              Math.round(depth * 10) / 10,
            ];
          }
        }
      }
      return best;
    };
  }

  /** Enter/leave the Commons: build or hide the sea. */
  private refresh(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const onCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    if (!onCommons) {
      if (this.mesh) this.mesh.visible = false;
      if (this.lakeMesh) this.lakeMesh.visible = false;
      this.leaveWater();
      return;
    }
    const commons = [...conn.db.world.iter()].find((w) => w.kind === 'commons');
    if (!commons) return;
    if (!this.mesh || this.seed !== commons.terrainSeed) {
      this.build(commons.terrainSeed);
    } else {
      this.mesh.visible = this.shoreline !== null && this.shoreline.maxDepth > 0.05;
    }
  }

  /** Fill scratch rows [from, to) with bed heights around (cx, cz). */
  private fillScratchRows(cx: number, cz: number, from: number, to: number): void {
    const n = SHORE_RES;
    for (let row = from; row < to; row++) {
      const z = (row / (n - 1) - 0.5) * FOOTPRINT + cz;
      for (let col = 0; col < n; col++) {
        const x = (col / (n - 1) - 0.5) * FOOTPRINT + cx;
        this.scratch[row * n + col] = meshHeight(this.seed, x, z);
      }
    }
  }

  /** One-time build (per seed): waves, shoreline bake, material, mesh. */
  private build(seed: string): void {
    this.disposeWater();
    this.seed = seed;
    this.seaLevel = commonsSeaLevel(seed);

    // Deterministic modest swell — same sea for every client.
    // count 8 (the createWaveSet max): the two extra cascade components
    // (~1.3 m / 0.8 m) live purely in the per-pixel normal (below the vertex
    // cutoff) and break up the "moving blanket" read — real seas carry many
    // superimposed frequencies (GPU Gems ch.1 / FFT-ocean canon).
    const waves = createWaveSet({ amplitude: 0.4, wavelength: 22, count: 8, steepness: 0.5, seed: 41 });
    this.waves = waves;
    // Interactive water: the camera-following interaction field every water
    // material samples (built for the tier now; the graphics flag only gates
    // whether it ever steps — the LIVE toggle needs the texture bound).
    const fx = waterFx();
    ensureWaterField(activeTier());
    fx.enabled = activePreset().fluidPhysics !== false;
    fx.provider = this.surfaceProvider();
    // Stage 3: the foam trails ride the SAME wave set the surface displaces
    // with (the Jacobian of these waves is where crests fold); the caustics
    // projector warps the same grid by the refracted sun.
    if (activePreset().waterFoam) {
      try { this.foam = new FoamAccumulator(waves, { sizeX: FOOTPRINT, sizeZ: FOOTPRINT, resolution: 128, persistence: 2.5, jacobianFull: 0.35, jacobianStart: 0.75, updateHz: 20 }); this.foam.setModulation(this.mod); } catch { this.foam = null; }
    }
    // The sea-state uniforms are created HERE so the caustics projector
    // (built before the material — its light map is bound at material build)
    // refracts the same wind-modulated surface the sea draws.
    const seaUniforms = { ampScale: uniform(1), dirRot: uniform(0) };
    const seaModNodes = { ampScale: seaUniforms.ampScale, dirCos: cos(seaUniforms.dirRot), dirSin: sin(seaUniforms.dirRot) };
    if (activePreset().waterCaustics) {
      try { this.caustics = new CausticsProjector({ sizeX: FOOTPRINT, sizeZ: FOOTPRINT, floorDepth: 6, resolution: 256, segments: 96, strength: 1.4, waves, waveModulation: seaModNodes as never }); } catch { this.caustics = null; }
    }

    // Initial bake (world load): dry-probe first (the spawn city is dry by
    // the settlement clamp); a wet spawn bakes in the WORKER — the old
    // synchronous fill cost ~0.5 s of load time.
    this.scratch.fill(this.seaLevel + 1000); // bone dry until a bake lands
    if (this.probeWet(this.centerX, this.centerZ)) {
      const worker = this.ensureWorker();
      if (worker) {
        this.pendingShoreId = ++this.jobId;
        this.pendingShoreAt = { x: this.centerX, z: this.centerZ };
        this.shoreJobsPosted++;
        const job: WaterBakeJob = {
          kind: 'shore', id: this.pendingShoreId, seed, cx: this.centerX, cz: this.centerZ, res: SHORE_RES, size: FOOTPRINT,
        };
        this.bakeBeatMs = performance.now();
        worker.postMessage(job);
      } else {
        this.fillScratchRows(this.centerX, this.centerZ, 0, SHORE_RES);
      }
    }
    this.shoreline = new ShorelineField({
      sizeX: FOOTPRINT,
      sizeZ: FOOTPRINT,
      waterLevel: this.seaLevel,
      // The engine samples EXACTLY the scratch lattice positions during
      // rebake — recover the texel index and read the pre-filled grid.
      bedHeight: (x, z) => {
        const n = SHORE_RES;
        const col = Math.round(((x - this.centerX) / FOOTPRINT + 0.5) * (n - 1));
        const row = Math.round(((z - this.centerZ) / FOOTPRINT + 0.5) * (n - 1));
        if (col >= 0 && col < n && row >= 0 && row < n) return this.scratch[row * n + col];
        return meshHeight(this.seed, x, z);
      },
      resolution: SHORE_RES,
      shoalDepth: 7,
      foamDepth: 1.4,
      offsetX: this.centerX,
      offsetZ: this.centerZ,
    });

    const normalMap = generateWaterNormalMap(256);
    this.normalMap = normalMap;
    this.handles = createRealisticWaterMaterial(
      {
        ...DEFAULT_REALISTIC_WATER,
        normalMap,
        // WebGPU doctrine: no viewport scene copies — r182's mid-pass copy
        // corrupts encoder state (engine RealisticWaterMaterialConfig docs).
        screenSpaceRefraction: !(this.app?.renderer.isWebGPU ?? true),
        // Planar reflections are an extra scene render — heavy; OFF by
        // default, now a graphics-menu opt-in (applies on next world entry).
        reflection: activePreset().waterReflections ?? false,
        skyColor: 0x87a8c3,
        causticIntensity: 0.25,
        // De-geometrize the horizon: displacement flattens toward the plane
        // rim so the sheet's edge reads as haze, never a sawtooth silhouette.
        distanceFade: { start: 860, end: 1380 },
        // Anti-tiling: geometry carries only the swell the 8 m grid can
        // sample; the five shorter cascade waves stay per-pixel (see the
        // SEGMENTS comment).
        vertexWavelengthCutoff: VERTEX_WAVE_CUTOFF,
        shoreline: {
          depthTexture: this.shoreline.texture,
          encodeMax: this.shoreline.encodeMax,
          shoalDepth: this.shoreline.shoalDepth,
          foamDepth: this.shoreline.foamDepth,
          shallowTint: 0xc2b280,
        },
        // Stage 3 textures (plane-UV addressed by the material; null = off).
        foamAccumTexture: this.foam ? this.foam.texture : null,
        causticTexture: this.caustics ? this.caustics.texture : null,
        // Interactive water: the wind's sea state as uniforms (shared with
        // the caustics), the interaction field in world XZ, rain rings.
        seaState: seaUniforms,
        interaction: { texture: fx.texture, size: fx.size },
        rainRipples: true,
      },
      waves,
    );
    this.buildSplashEmitter();

    const geo = new THREE.PlaneGeometry(FOOTPRINT, FOOTPRINT, SEGMENTS, SEGMENTS);
    geo.rotateX(-Math.PI / 2);
    this.mesh = new THREE.Mesh(geo, this.handles.material);
    this.mesh.name = 'CommonsSea';
    this.mesh.frustumCulled = false; // displaced in the vertex stage
    this.mesh.position.set(this.centerX, this.seaLevel, this.centerZ);
    this.handles.uWorldOffset.value.set(this.centerX, this.centerZ);
    this.mesh.visible = this.shoreline.maxDepth > 0.05;
    this.scene.add(this.mesh);
    // Planar reflections: three's reflector mirrors across the plane through
    // its TARGET (normal = the target's local +Z; the engine pre-rotates it so
    // that is the sheet's +Y). Seated under the mesh it follows every recenter
    // at sea level. Orphaned — as it was until 2026-09-10 — it sat at the FP64
    // rebase origin facing +Z: a VERTICAL mirror through the spawn point. The
    // player saw their own camera view stamped on the sea on one side of it
    // and black water (three clears a "facing away" reflection) on the other.
    if (this.handles.reflectorTarget) this.mesh.add(this.handles.reflectorTarget);

    // ── Lake layer: calm short waves, muddy shallow tint, own depth texture
    // (per-texel depth = surface − bed, so one mesh carries every lake in
    // the region at its own level).
    this.lakeDepthTex = new THREE.DataTexture(this.lakeBytes, LAKE_GRID_N, LAKE_GRID_N, THREE.RedFormat);
    this.lakeDepthTex.wrapS = THREE.ClampToEdgeWrapping;
    this.lakeDepthTex.wrapT = THREE.ClampToEdgeWrapping;
    this.lakeDepthTex.magFilter = THREE.LinearFilter;
    this.lakeDepthTex.minFilter = THREE.LinearFilter;
    this.lakeDepthTex.colorSpace = THREE.NoColorSpace;
    const lakeWaves = createWaveSet({ amplitude: 0.05, wavelength: 7, count: 4, steepness: 0.35, seed: 97 });
    this.lakeWaves = lakeWaves;
    this.lakeHandles = createRealisticWaterMaterial(
      {
        ...DEFAULT_REALISTIC_WATER,
        normalMap,
        // WebGPU doctrine: no viewport scene copies (see the sea material).
        screenSpaceRefraction: !(this.app?.renderer.isWebGPU ?? true),
        reflection: activePreset().waterReflections ?? false, // menu opt-in (next world entry)
        skyColor: 0x87a8c3,
        causticIntensity: 0.3,
        seaState: true,
        interaction: { texture: fx.texture, size: fx.size },
        rainRipples: true,
        // Anti-tiling: the lake grid is 6 m cells and ALL four lake waves
        // (7 m cascade) sit below its Nyquist limit — displace none of them
        // (5 cm amplitude; the per-pixel normal carries the ripples).
        vertexWavelengthCutoff: 2.5 * (LAKE_REGION_SIZE / (LAKE_GRID_N - 1)),
        shoreline: {
          depthTexture: this.lakeDepthTex,
          encodeMax: AtlasWaterModule.LAKE_ENCODE_MAX,
          shoalDepth: 2.5,
          foamDepth: 0.45,
          shallowTint: 0x9a8c66,
        },
      },
      lakeWaves,
    );
    // Placeholder geometry: an empty BufferGeometry has no vertex layout for
    // pipeline creation — the pre-warm below needs a real (tiny) grid.
    const lakePlaceholder = new THREE.PlaneGeometry(1, 1, 1, 1);
    lakePlaceholder.rotateX(-Math.PI / 2);
    this.lakeMesh = new THREE.Mesh(lakePlaceholder, this.lakeHandles.material);
    this.lakeMesh.name = 'CommonsLakes';
    this.lakeMesh.frustumCulled = false;
    this.lakeMesh.visible = false; // until the first region bake lands
    this.scene.add(this.lakeMesh);
    // Same seating as the sea; the lake mesh sits at y = 0 with per-vertex
    // levels, so the target's local y is lifted to the nearest lake's level
    // every few frames (seatLakeMirror).
    if (this.lakeHandles.reflectorTarget) this.lakeMesh.add(this.lakeHandles.reflectorTarget);
    this.lakeCenterX = Infinity;
    this.lakeCenterZ = Infinity;

    // Submerged-camera screen effect — built NOW (not on first submerge) so
    // its pipeline can be pre-warmed below; first-use compilation at the
    // submerge moment was half of the 1–2 s hitch.
    this.overlay = new UnderwaterOverlay({ deepColor: 0x03212e, shallowColor: 0x2e8b8b });
    this.scene.add(this.overlay.mesh);
    if (activePreset().underwaterShafts) {
      try {
        this.shafts = createUnderwaterShafts({ count: 14, length: 40, width: 7, radius: 12, color: [0.5, 0.82, 0.92], intensity: 1, depthScale: 14 });
        this.shafts.mesh.visible = false;
        this.scene.add(this.shafts.mesh);
        stage4Stats.shaftsBuilt++;
      } catch { this.shafts = null; }
    }

    // Pre-warm ALL water pipelines during world load (Renderer.compileAsync):
    // meshes flip visible with the overlay at zero intensity for the compile
    // pass (compile may skip invisibles), then restore.
    try {
      const native = this.app?.renderer.native as unknown as {
        compileAsync?: (o: unknown, c?: unknown, s?: unknown) => Promise<unknown>;
      };
      if (native?.compileAsync) {
        let camera: THREE.Camera | undefined;
        try {
          camera = this.app?.renderer.cameras.main;
        } catch {
          camera = undefined;
        }
        const overlay = this.overlay;
        overlay.uIntensity.value = 0; // effect invisible even while warming
        const meshes = [this.mesh, this.lakeMesh, overlay.mesh];
        const restore = meshes.map((m) => m.visible);
        for (const m of meshes) m.visible = true;
        void Promise.allSettled(
          meshes.map((m) => native.compileAsync!(m, camera, this.scene)),
        ).then(() => {
          meshes.forEach((m, i) => { m.visible = this.submerged && m === overlay.mesh ? m.visible : restore[i]; });
          if (this.mesh && this.shoreline) this.mesh.visible = this.shoreline.maxDepth > 0.05;
          overlay.uIntensity.value = 1;
        });
      }
    } catch { /* renderer without compileAsync */ }
  }

  /** Swap the lake mesh over to a freshly baked region. */
  private applyLakeRegion(region: LakeRegion): void {
    if (!this.lakeMesh || !this.lakeHandles || !this.lakeDepthTex) return;
    this.lakeCenterX = region.centerX;
    this.lakeCenterZ = region.centerZ;
    if (region.maxDepth <= 0) {
      this.lakeMesh.visible = false;
      return;
    }
    const n = LAKE_GRID_N;
    // Per-texel depth drives shoaling/foam/tint in the shared water shader.
    for (let i = 0; i < n * n; i++) {
      const s = region.surface[i];
      const depth = Number.isNaN(s) ? 0 : Math.max(0, s - region.raw[i]);
      this.lakeBytes[i] = Math.round(Math.min(1, depth / AtlasWaterModule.LAKE_ENCODE_MAX) * 255);
    }
    this.lakeDepthTex.needsUpdate = true;

    // Region plane: wet nodes sit AT their lake surface; dry nodes sink
    // below the terrain so nothing of them ever shows.
    const geo = new THREE.PlaneGeometry(LAKE_REGION_SIZE, LAKE_REGION_SIZE, n - 1, n - 1);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const col = Math.round((pos.getX(i) / LAKE_REGION_SIZE + 0.5) * (n - 1));
      const row = Math.round((pos.getZ(i) / LAKE_REGION_SIZE + 0.5) * (n - 1));
      const idx = row * n + col;
      const s = region.surface[idx];
      // Dry nodes tuck JUST under the terrain (0.35 m) — the old 3 m sink
      // wrapped every lake in a polygonal cliff skirt, the 'geometric rim'
      // visible from any distance.
      pos.setY(i, Number.isNaN(s) ? region.raw[idx] - 0.35 : s);
    }
    const old = this.lakeMesh.geometry;
    this.lakeMesh.geometry = geo;
    disposeLater(old); // swapped inside tick(): the in-flight submit may still reference it
    this.lakeMesh.position.set(region.centerX, 0, region.centerZ);
    this.lakeHandles.uWorldOffset.value.set(region.centerX, region.centerZ);
    this.lakeMesh.visible = true;
    this.lakeRegion = region;
    this.seatLakeMirror();
  }

  /**
   * Lift the lakes' mirror plane to the level of the lake nearest the camera.
   * One region mesh carries every lake at its own height (vertex y), so a
   * single planar reflector can only be exact for one of them — the one the
   * player is at or looking toward. Cheap (a 65×65 subsample), run every
   * 15 frames and on each region swap.
   */
  private seatLakeMirror(): void {
    const target = this.lakeHandles?.reflectorTarget;
    if (!target || !this.lakeRegion) return;
    const level = nearestLakeLevel(this.lakeRegion, this.camPos.x, this.camPos.z);
    if (level !== null) target.position.y = level;
  }

  /** Planar-reflection diagnostics for the probe (parts null when reflections are off). */
  /** The interactive-water state for probes (`__water().fluid`). */
  private fluidState(): object {
    const fx = waterFx();
    return {
      enabled: fx.enabled, tier: activeTier(), field: fx.spec, fieldActive: fx.field?.isActive ?? false, origin: this.fxStats.fieldOrigin,
      stamps: fx.stats.stamps, rain: fx.stats.rain, steps: fx.stats.steps, splashes: fx.stats.splashes,
      seaAmp: Math.round(this.fxStats.seaAmp * 1000) / 1000, seaRotDeg: Math.round((this.fxStats.seaRot * 180) / Math.PI * 10) / 10,
      foamJ: Math.round(this.fxStats.foamJ * 1000) / 1000, target: this.seaState,
      rainIntensity: this.handles ? Math.round(this.handles.uRainIntensity.value * 100) / 100 : 0,
      drift: this.handles ? [Math.round(this.handles.uDetailDrift.value.x * 100) / 100, Math.round(this.handles.uDetailDrift.value.y * 100) / 100] : null,
      splashEmitter: this.splashEmitter ? this.splashEmitter.backend : null,
      // Probe helpers: stamp and read at ABSOLUTE coordinates.
      heightAt: (x: number, z: number): number => fx.field?.heightAtWorld(x, z) ?? 0,
      liveAt: (x: number, z: number): number | null => fx.provider?.liveAt(x, z) ?? null,
      stillAt: (x: number, z: number): number | null => fx.provider?.stillAt(x, z) ?? null,
      disturb: (x: number, z: number, strength = -0.05, radius = 0.5): boolean => { if (!fx.enabled || !fx.field) return false; fx.field.disturbWorld(x, z, strength, radius); fx.stats.stamps++; return true; },
      splash: (x: number, y: number, z: number, count = 40): boolean => { if (!fx.splash || !fx.enabled) return false; fx.splash(x, y, z, count, 3); fx.stats.splashes++; return true; },
    };
  }

  private reflectionState(): object {
    const describe = (h: RealisticWaterHandles | null, mesh: THREE.Mesh | null): object | null => {
      if (!h || !h.reflectorTarget || !mesh) return null;
      const t = h.reflectorTarget;
      t.updateWorldMatrix(true, false);
      this._mirrorN.set(0, 0, 1).transformDirection(t.matrixWorld); // three's reflector normal: the target's local +Z
      t.getWorldPosition(this._mirrorP);
      const base = h.reflectorNode?.reflector as unknown as {
        hasOutput?: boolean;
        bounces?: boolean;
        virtualCameras?: WeakMap<object, THREE.Camera>;
        renderTargets?: Map<object, { width: number; height: number }>;
      } | undefined;
      // The virtual camera is repositioned ONLY inside a reflection render
      // (ReflectorNode.updateBefore) — `hasOutput` alone can be left true by
      // the boot pre-warm. Its y must mirror the camera's under the plane.
      let virtualCamY: number | null = null;
      let rt: string | null = null;
      try {
        const cam = this.app?.renderer.cameras.main;
        if (!cam) throw new Error("no camera");
        const vc = base?.virtualCameras?.get(cam);
        if (vc) {
          virtualCamY = Math.round((vc.position.y - this.scene.position.y) * 100) / 100;
          const target = base?.renderTargets?.get(vc);
          if (target) rt = `${target.width}x${target.height}`;
        }
      } catch { /* no main camera yet */ }
      return {
        parented: t.parent === mesh,
        normalY: Math.round(this._mirrorN.y * 1000) / 1000,
        planeY: Math.round((this._mirrorP.y - this.scene.position.y) * 100) / 100, // absolute (the scene carries the rebase shift)
        hasOutput: base?.hasOutput ?? null,
        bounces: base?.bounces ?? null,
        virtualCamY,
        rt,
      };
    };
    return {
      on: activePreset().waterReflections ?? false,
      sea: describe(this.handles, this.mesh),
      lake: describe(this.lakeHandles, this.lakeMesh),
    };
  }

  /** Advance the sliced lake bake (runs only when the sea bake is idle). */
  private stepLakeBake(): void {
    if (!this.lakeBake) return;
    const ROWS_PER_FRAME = 2;
    this.lakeBakeRow = this.lakeBake.fillRows(this.lakeBakeRow, this.lakeBakeRow + ROWS_PER_FRAME);
    if (this.lakeBakeRow < LAKE_GRID_N) return;
    const region = this.lakeBake.finalize();
    this.lakeBake = null;
    this.applyLakeRegion(region);
  }

  /** Lazily build the bake worker; sliced fallback when unavailable. */
  private ensureWorker(): Worker | null {
    if (this.workerBroken) return null;
    if (this.bakeWorker) return this.bakeWorker;
    try {
      this.bakeWorker = new Worker(new URL('../workers/water_bake_worker.ts', import.meta.url), { type: 'module' });
      if (this.seed) postRiverTable(this.bakeWorker, this.seed); // shared network (perf frontier item 12)
      this.bakeWorker.onerror = () => {
        this.workerBroken = true;
        this.bakeWorker?.terminate();
        this.bakeWorker = null;
      };
      this.bakeWorker.onmessage = (e: MessageEvent<WaterBakeResult>) => {
        this.bakeBeatMs = performance.now();
        this.bakeEverReplied = true;
        this.onBakeResult(e.data);
      };
    } catch {
      this.workerBroken = true;
      this.bakeWorker = null;
    }
    return this.bakeWorker;
  }

  /** Apply a worker bake (stale responses from superseded targets dropped). */
  private onBakeResult(r: WaterBakeResult): void {
    if (r.kind === 'shore') {
      if (r.id !== this.pendingShoreId || !this.mesh || !this.shoreline || !this.handles) { this.shoreRepliesDropped++; return; }
      this.pendingShoreId = 0;
      this.pendingShoreAt = null;
      this.scratch.set(r.heights);
      this.centerX = r.cx;
      this.centerZ = r.cz;
      this.shoreline.rebake(r.cx, r.cz);
      this.mesh.position.set(r.cx, this.seaLevel, r.cz);
      this.handles.uWorldOffset.value.set(r.cx, r.cz);
      this.mesh.visible = this.shoreline.maxDepth > 0.05;
      return;
    }
    if (r.kind !== 'lake' || r.id !== this.pendingLakeId) return;
    this.pendingLakeId = 0;
    const region: LakeRegion = {
      centerX: r.centerX,
      centerZ: r.centerZ,
      raw: r.raw,
      surface: r.surface,
      maxDepth: r.maxDepth,
    };
    insertLakeRegion(region);
    this.applyLakeRegion(region);
  }

  /** 8×8 dry-probe: fully-dry footprints skip the fine bake entirely. */
  private probeWet(x: number, z: number): boolean {
    for (let r = 0; r < 8; r++) {
      const pz = (r / 7 - 0.5) * FOOTPRINT + z;
      for (let c = 0; c < 8; c++) {
        const px = (c / 7 - 0.5) * FOOTPRINT + x;
        if (meshHeight(this.seed, px, pz) < this.seaLevel + 2) return true;
      }
    }
    return false;
  }

  /** Queue a recenter — worker bake, or sliced rows when no Worker exists. */
  private requestRecenter(x: number, z: number): void {
    if (!this.mesh || !this.shoreline || !this.handles) return;
    const sx = Math.round(x / SNAP) * SNAP;
    const sz = Math.round(z / SNAP) * SNAP;
    if (sx === this.centerX && sz === this.centerZ) return;
    if (this.bakeTarget && this.bakeTarget.x === sx && this.bakeTarget.z === sz) return;

    // Inland travel (the common case): just move the hidden footprint. The
    // probe (64 uncached terrain samples) is memoized per snap point: while a
    // shore bake was in flight the distance check re-entered EVERY tick and
    // re-probed the same point — 4–17 ms a frame for the whole bake (2026-09-12).
    let wet: boolean;
    if (this.lastProbe && this.lastProbe.x === sx && this.lastProbe.z === sz) wet = this.lastProbe.wet;
    else { wet = this.probeWet(sx, sz); this.lastProbe = { x: sx, z: sz, wet }; }
    if (!wet) {
      this.bakeTarget = null;
      this.pendingShoreId = 0;
      this.centerX = sx;
      this.centerZ = sz;
      this.mesh.position.set(sx, this.seaLevel, sz);
      this.handles.uWorldOffset.value.set(sx, sz);
      this.mesh.visible = false;
      return;
    }

    const worker = this.ensureWorker();
    if (worker) {
      // ONE shore job in flight. The distance check fires every tick until a
      // reply moves the centre; posting a fresh job each tick (a new id, the
      // older replies dropped as stale) queued a 320² bake per FRAME behind a
      // worker that finishes one every few hundred ms — the newest reply never
      // came, the sea never re-baked after a teleport or a long ride, and the
      // per-dispatch heartbeat kept the stall watchdog asleep (2026-09-10).
      // The reply re-arms the distance check if the player is still far.
      if (this.pendingShoreId !== 0) return;
      this.pendingShoreId = ++this.jobId;
      this.pendingShoreAt = { x: sx, z: sz };
      this.shoreJobsPosted++;
      const job: WaterBakeJob = {
        kind: 'shore', id: this.pendingShoreId, seed: this.seed, cx: sx, cz: sz, res: SHORE_RES, size: FOOTPRINT,
      };
      this.bakeBeatMs = performance.now();
      worker.postMessage(job);
      return;
    }
    this.bakeTarget = { x: sx, z: sz };
    this.bakeRow = 0;
  }

  /** FALLBACK sliced bake (no Worker); the dry-probe ran at request time. */
  private stepBake(): void {
    if (!this.bakeTarget || !this.mesh || !this.shoreline || !this.handles) return;
    const ROWS_PER_FRAME = 3; // ~43 frames per recenter, a few ms each
    const { x, z } = this.bakeTarget;
    const end = Math.min(SHORE_RES, this.bakeRow + ROWS_PER_FRAME);
    // Rows are sampled around the TARGET center; the closure maps via
    // this.centerX/Z, which flips to the target just before the rebake.
    this.fillScratchRows(x, z, this.bakeRow, end);
    this.bakeRow = end;
    if (end < SHORE_RES) return;
    this.centerX = x;
    this.centerZ = z;
    this.shoreline.rebake(x, z);
    this.mesh.position.set(x, this.seaLevel, z);
    this.handles.uWorldOffset.value.set(x, z);
    // A fully dry footprint (inland) renders nothing at all.
    this.mesh.visible = this.shoreline.maxDepth > 0.05;
    this.bakeTarget = null;
  }

  private tick(dt: number): void {
    if (!this.mesh || !this.handles || !this.app) return;
    if (atlas.currentWorldId !== atlas.commonsWorldId) return;

    // One clock for the shaders; sun follows the sky module's uniform.
    this.time = (this.time + dt) % (Math.PI * 2 * 1000);
    this.handles.uTime.value = this.time;
    // The wave field is evaluated one frame back for positionPrevious — feed
    // it the REAL delta so the velocity buffer matches a variable frame rate.
    this.handles.uFrameDt.value = Math.min(0.1, Math.max(1 / 240, dt));
    // Stage 3: refresh the foam grid at its own cadence (20 Hz, decay exact)
    // over the sea's current centre; render the caustics every other frame
    // (its own RT, before the main pass — this tick is RenderSub/36).
    if (this.foam && this.mesh.visible && this.foam.update(this.time, dt, this.centerX, this.centerZ)) stage3Stats.foamUpdates++;
    if (this.caustics && this.mesh.visible && (++this.causticFrame & 1) === 0) {
      try { this.caustics.render(this.app.renderer.native as never, this.time); stage3Stats.causticRenders++; } catch { this.caustics = null; }
    }
    this.tickFluid(dt);
    this.handles.uSunDirection.value.copy(uSunDir.value as THREE.Vector3);
    // Authored water radiance follows the sun elevation (floor = moonlit sea)
    // — a day-constant sea GLOWS at midnight (the fog/clouds night-bug family).
    const sunY = (uSunDir.value as THREE.Vector3).y;
    const illum = waterIlluminance(sunY); // ONE rule for sea, lakes and rivers (lib/water_lighting)
    this.handles.uIlluminance.value = illum;
    // Sky-side tint tracks the hour (reflection is off — the fallback is a
    // uniform): warm low-sun band around dawn/dusk, deep navy at night.
    waterSkyTint(sunY, this._skyTint);
    this.handles.uSkyColor.value.copy(this._skyTint);
    if (this.lakeHandles) {
      this.lakeHandles.uTime.value = this.time;
      this.lakeHandles.uFrameDt.value = Math.min(0.1, Math.max(1 / 240, dt));
      this.lakeHandles.uSunDirection.value.copy(uSunDir.value as THREE.Vector3);
      this.lakeHandles.uIlluminance.value = illum;
      this.lakeHandles.uSkyColor.value.copy(this._skyTint);
    }

    let camera: THREE.Camera | undefined;
    try {
      camera = this.app.renderer.cameras.main;
    } catch {
      camera = undefined;
    }
    if (!camera) return;
    camera.getWorldPosition(this.camPos);
    // FP64 origin rebase (AtlasOriginRebaseModule) holds scene.position at
    // MINUS-the-origin while children carry ABSOLUTE coords — so a world-space
    // camera read comes back origin-shifted. Convert back to absolute before
    // any recenter math or terrainHeight sampling, or the first rebase beyond
    // 10 km re-bakes the sea/lakes at the wrong spot and hides them (the
    // "water vanished after a second at the ocean" bug).
    this.camPos.x -= this.scene.position.x;
    this.camPos.z -= this.scene.position.z;

    // Wave phase must match the FRAGMENT stage, which evaluates normals/foam
    // from positionWorld — origin-shifted under the rebase. Feed uWorldOffset
    // in the SAME rebased space every frame (phase re-anchors once per 10 km
    // rebase — the same accepted nit as the ground macro noise).
    this.handles.uWorldOffset.value.set(
      this.centerX + this.scene.position.x,
      this.centerZ + this.scene.position.z,
    );
    if (this.lakeHandles && Number.isFinite(this.lakeCenterX)) {
      this.lakeHandles.uWorldOffset.value.set(
        this.lakeCenterX + this.scene.position.x,
        this.lakeCenterZ + this.scene.position.z,
      );
    }

    // Stall watchdog: a bake worker wedged mid-import (cold dev-server
    // dep-optimize) fires no 'error' — the lake gate (`!pendingLakeId`) would
    // block forever and a spawn shore bake would leave the sea bone-dry.
    // Silence with a job in flight for 20 s ⇒ broken; sliced fallbacks resume.
    // BOOT vs stall budgets (the chunk worker's law): a cold worker legitimately
    // takes tens of seconds to import its graph on a dev server — 20 s there
    // abandoned a healthy worker for the session ("bake worker stalled — sliced
    // main-thread bakes take over" on a real machine, perf sweep 2026-08-22).
    const stallBudget = this.bakeEverReplied ? 20_000 : 90_000;
    if ((this.pendingShoreId !== 0 || this.pendingLakeId !== 0) && performance.now() - this.bakeBeatMs > stallBudget) {
      this.workerBroken = true;
      this.bakeWorker?.terminate();
      this.bakeWorker = null;
      if (this.pendingShoreId !== 0) {
        this.pendingShoreId = 0;
        // Redo the LOST bake, sliced — at the target it was for, not at the
        // stale centre (which sliced a footprint the player had already left).
        this.bakeTarget = this.pendingShoreAt ?? { x: this.centerX, z: this.centerZ };
        this.pendingShoreAt = null;
        this.bakeRow = 0;
      }
      if (this.pendingLakeId !== 0) {
        this.pendingLakeId = 0;
        this.lakeCenterX = Infinity; // re-arm the lake gate for the sliced path
        this.lakeCenterZ = Infinity;
      }
      console.warn('[AtlasWater] bake worker stalled — sliced main-thread bakes take over');
    }

    if (
      Math.abs(this.camPos.x - this.centerX) > REBAKE_DIST ||
      Math.abs(this.camPos.z - this.centerZ) > REBAKE_DIST
    ) {
      this.requestRecenter(this.camPos.x, this.camPos.z);
    }

    // Lake region follows the player too (quarter-region hysteresis) —
    // baked in the worker; sliced main-thread only as the fallback.
    if (
      !this.lakeBake &&
      !this.pendingLakeId &&
      (Math.abs(this.camPos.x - this.lakeCenterX) > LAKE_REGION_SIZE / 4 ||
        Math.abs(this.camPos.z - this.lakeCenterZ) > LAKE_REGION_SIZE / 4)
    ) {
      const sx = Math.round(this.camPos.x / SNAP) * SNAP;
      const sz = Math.round(this.camPos.z / SNAP) * SNAP;
      const worker = this.ensureWorker();
      if (worker) {
        this.pendingLakeId = ++this.jobId;
        // Optimistically mark the region as current so the gate doesn't
        // re-fire every tick while the worker runs; a supersede re-posts.
        this.lakeCenterX = sx;
        this.lakeCenterZ = sz;
        const job: WaterBakeJob = { kind: 'lake', id: this.pendingLakeId, seed: this.seed, cx: sx, cz: sz };
        this.bakeBeatMs = performance.now();
        worker.postMessage(job);
      } else {
        this.lakeBake = createLakeBake(this.seed, sx, sz);
        this.lakeBakeRow = 0;
      }
    }
    if (this.bakeTarget) this.stepBake();
    else this.stepLakeBake();
    if (++this.mirrorTick % 15 === 0) this.seatLakeMirror();

    // Submerged? Below the sea surface over actual sea, or under a lake.
    const overWater =
      this.mesh.visible &&
      this.shoreline !== null &&
      this.shoreline.depthAt(this.camPos.x - this.centerX, this.camPos.z - this.centerZ) > 0.05;
    const riverSurface = this.seed ? riverWaterAt(this.seed, this.camPos.x, this.camPos.z) : null;
    // A river channel counts as a lake at its water level (terrain wave 2026-09-08).
    const lakeSurface = lakeSurfaceAtBaked(this.camPos.x, this.camPos.z) ?? riverSurface;
    const underSea = overWater && this.camPos.y < this.seaLevel;
    // Symmetric with the sea: a hidden lake mesh is no lake (the perf harness
    // surfaces a probe by hiding the water meshes; lakes landed after that trick).
    const underLake = ((this.lakeMesh?.visible ?? false) || riverSurface !== null) && lakeSurface !== null && this.camPos.y < lakeSurface - 0.05;
    const under = underSea || underLake;
    if (under && !this.submerged) {
      // Fog goes underwater by MUTATING the atmosphere's own FogExp2 —
      // swapping the scene.fog object rebuilt every material (the 1–2 s
      // submerge/surface hitch). The overlay was pre-built + pre-warmed in
      // build(), so this toggle costs nothing.
      setUnderwaterFog({ color: 0x06222e, density: 0.045 });
      if (this.overlay) this.overlay.mesh.visible = true;
      sfx('sfx_water_splash', { gain: 0.5, cooldownMs: 600 }); // breaking the surface
      this.submerged = true;
      atlas.submerged = true; // ambience + any other module reads the shared flag
    } else if (!under && this.submerged) {
      this.leaveWater();
    }
    if (under) atlas.submerged = true; // re-assert each frame: a water DEVICE leaving its volume writes false on its own transition
    if (under && this.overlay) {
      const surfaceY = underLake && lakeSurface !== null ? lakeSurface : this.seaLevel;
      this.overlay.update(this.time, surfaceY - this.camPos.y);
      if (this.shafts) {
        // The fan rides the camera (scene space); its streaks lean with the sun.
        const m = this.shafts.mesh;
        m.position.set(this.camPos.x, this.camPos.y + 8, this.camPos.z); // camPos is already ABSOLUTE (the rebase note above)
        const sd = uSunDir.value as THREE.Vector3;
        m.rotation.y = Math.atan2(sd.x, sd.z);
        this.shafts.uniforms.slant.value.set(Math.hypot(sd.x, sd.z) / Math.max(0.05, sd.y) * 0.25, 0);
        this.shafts.uniforms.depth.value = Math.max(0, surfaceY - this.camPos.y);
        this.shafts.uniforms.intensity.value = Math.max(0, sd.y) * 1.2; // no beams at night
        m.visible = true;
        stage4Stats.shaftsVisible = true;
      }
    }
  }

  // ─── Interactive water ────────────────────────────────────────────────────

  /** The surface every stamper asks: still level, live height, live slope — ABSOLUTE coords. */
  private surfaceProvider(): WaterSurfaceProvider {
    const seaStill = (x: number, z: number): number | null => {
      if (!this.mesh || !this.mesh.visible || !this.shoreline) return null;
      const lx = x - this.centerX, lz = z - this.centerZ;
      if (Math.abs(lx) > FOOTPRINT * 0.5 || Math.abs(lz) > FOOTPRINT * 0.5) return null;
      return this.shoreline.depthAt(lx, lz) > 0.05 ? this.seaLevel : null;
    };
    // Rivers are COMMONS rivers: off-world the channel field still answered
    // for any coordinate under a Commons reach — wading on dry ground in a
    // personal world whose plot happened to overlap one (2026-09-12).
    const lakeStill = (x: number, z: number): number | null =>
      ((this.lakeMesh?.visible ?? false) ? lakeSurfaceAtBaked(x, z) : null)
      ?? (this.seed && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId ? riverWaterAt(this.seed, x, z) : null);
    // The shaders evaluate the wave phase at RENDER-space coordinates
    // (uWorldOffset carries the rebase) — the CPU query must too.
    const rx = (x: number): number => x + this.scene.position.x;
    const rz = (z: number): number => z + this.scene.position.z;
    const seaShoal = (x: number, z: number): number => {
      if (!this.shoreline) return 1;
      const d = this.shoreline.depthAt(x - this.centerX, z - this.centerZ);
      const t = Math.min(1, Math.max(0, d / this.shoreline.shoalDepth));
      return t * t * (3 - 2 * t);
    };
    const onCommons = (): boolean => this.seed !== '' && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    return {
      // The order is lakeStill's own: a baked lake first, then a river channel, then the sea.
      kindAt: (x, z) => ((this.lakeMesh?.visible ?? false) && lakeSurfaceAtBaked(x, z) !== null ? 'lake'
        : onCommons() && riverWaterAt(this.seed, x, z) !== null ? 'river'
          : seaStill(x, z) !== null ? 'sea' : null),
      seaAmp: () => this.mod.ampScale,
      stillAt: (x, z) => lakeStill(x, z) ?? seaStill(x, z),
      liveAt: (x, z) => {
        const lake = lakeStill(x, z);
        const ripple = waterFx().field?.heightAtWorld(x, z) ?? 0;
        if (lake !== null) return lake + sampleHeight(this.lakeWaves, rx(x), rz(z), this.time, this.lakeMod) + ripple;
        const sea = seaStill(x, z);
        if (sea === null) return null;
        return sea + sampleHeight(this.waves, rx(x), rz(z), this.time, this.mod) * seaShoal(x, z) + ripple;
      },
      slopeAt: (x, z) => {
        const lake = lakeStill(x, z);
        const r = waterFx().field?.slopeAtWorld(x, z) ?? { dx: 0, dz: 0 };
        const n = lake !== null
          ? sampleNormal(this.lakeWaves, rx(x), rz(z), this.time, this.lakeMod)
          : seaStill(x, z) !== null ? sampleNormal(this.waves, rx(x), rz(z), this.time, this.mod) : { nx: 0, ny: 1, nz: 0 };
        const k = lake !== null ? 1 : seaShoal(x, z);
        return { dx: (-n.nx / Math.max(0.2, n.ny)) * k + r.dx, dz: (-n.nz / Math.max(0.2, n.ny)) * k + r.dz };
      },
    };
  }

  /**
   * Per frame: the wind's sea state (slewed, never a jump) into the wave
   * uniforms, the foam threshold and the detail drift; the interaction field
   * follows the camera and steps; rain stamps drops where there is water.
   */
  private tickFluid(dt: number): void {
    if (!this.handles) return;
    const fx = waterFx();
    fx.enabled = activePreset().fluidPhysics !== false;
    if (!this.weather) this.weather = (this.app?.getModule?.('Weather') as WeatherModule | undefined) ?? null;
    const w = this.weather?.wind() ?? { x: 1, z: 0, strength: 0.45, kind: 'none', density: 0 };
    const storm = w.kind === 'rain' ? w.density : 0;
    // ── Sea state from the wind (only with fluid physics on; off = the authored sea exactly).
    const target = fx.enabled ? seaStateFor(w.strength, storm) : seaStateFor(0.45);
    const d = Math.min(0.1, Math.max(0, dt));
    this.mod.ampScale = slew(this.mod.ampScale, target.ampScale, AMP_SLEW_PER_S, d);
    const primary = this.waves[0];
    const rotTarget = fx.enabled && primary ? windHeadingRotation(primary.dirX, primary.dirZ, w.x, w.z) : 0;
    this.mod.dirRot = slewAngle(this.mod.dirRot, rotTarget, HEADING_SLEW_RAD_PER_S, d);
    this.seaState = target;
    this.lakeMod.ampScale = 1 + (this.mod.ampScale - 1) * 0.5;
    this.lakeMod.dirRot = this.mod.dirRot;
    const foamJ = slew(this.handles.uFoamJacobian.value, target.foamJacobian, 0.1, d);
    this.handles.uWaveAmpScale.value = this.mod.ampScale;
    this.handles.uWaveDirRot.value = this.mod.dirRot;
    this.handles.uFoamJacobian.value = foamJ;
    // Detail ripples drift downwind at the authored magnitude (|(1, 0.8)| = 1.28) × the sea state.
    const driftMag = 1.28 * target.driftScale;
    if (fx.enabled) this._windDrift.set(w.x, w.z).normalize().multiplyScalar(driftMag);
    else this._windDrift.set(1, 0.8);
    this.handles.uDetailDrift.value.lerp(this._windDrift, Math.min(1, d * 0.5));
    const rain = fx.enabled && w.kind === 'rain' ? w.density : 0;
    this.handles.uRainIntensity.value += (rain - this.handles.uRainIntensity.value) * Math.min(1, d * 2);
    if (this.lakeHandles) {
      this.lakeHandles.uWaveAmpScale.value = this.lakeMod.ampScale;
      this.lakeHandles.uWaveDirRot.value = this.lakeMod.dirRot;
      this.lakeHandles.uFoamJacobian.value = foamJ;
      this.lakeHandles.uDetailDrift.value.copy(this.handles.uDetailDrift.value);
      this.lakeHandles.uRainIntensity.value = this.handles.uRainIntensity.value;
    }
    // ── The interaction field follows the camera (whole-cell snaps keep the rings world-anchored).
    const field = fx.field;
    if (field) {
      field.setOrigin(this.camPos.x, this.camPos.z);
      const o = field.origin;
      this.fxStats.fieldOrigin = [o.x, o.z];
      // The materials sample in RENDER space: origin + the rebase shift.
      this.handles.uInteractionCenter.value.set(o.x + this.scene.position.x, o.z + this.scene.position.z);
      if (this.lakeHandles) this.lakeHandles.uInteractionCenter.value.copy(this.handles.uInteractionCenter.value);
      fx.centerRender.x = this.handles.uInteractionCenter.value.x;
      fx.centerRender.z = this.handles.uInteractionCenter.value.y;
      fx.rain = this.handles.uRainIntensity.value;
      if (fx.enabled) {
        // Rain: the drops big enough to ring outward, stamped only over water.
        if (rain > 0 && fx.provider) {
          const n = rainDropsThisTick(rain, fx.size, d, this.rainDebt);
          for (let i = 0; i < n; i++) {
            const x = o.x + (Math.random() - 0.5) * fx.size * 0.9;
            const z = o.z + (Math.random() - 0.5) * fx.size * 0.9;
            if (fx.provider.stillAt(x, z) === null) continue;
            field.disturbWorld(x, z, RAIN_DROP_STAMP.strength, RAIN_DROP_STAMP.radius);
            fx.stats.rain++;
          }
        }
        if (field.isActive) { field.update(d); fx.stats.steps++; }
      }
    }
    if (this.splashEmitter) this.splashEmitter.update(d);
    this.fxStats.seaAmp = this.mod.ampScale; this.fxStats.seaRot = this.mod.dirRot; this.fxStats.foamJ = foamJ;
  }

  /** One pooled burst emitter for every splash (a body entering the water). */
  private buildSplashEmitter(): void {
    const app = this.app;
    if (!app || (activePreset().particleCap ?? 0) <= 0) return;
    if (this.splashEmitter) { this.scene.remove(this.splashEmitter.mesh); this.splashEmitter = null; }
    const cfg = {
      maxParticles: 600, lifetime: { min: 0.45, max: 0.9 }, startSpeed: { min: 2, max: 5.5 }, startSize: { min: 0.07, max: 0.22 },
      startColor: 0xe8f2f8, simulationSpace: 'world', shape: { type: 'cone', angle: 0.7, radius: 0.35, height: 0.15 },
      emission: { rateOverTime: 0 }, forces: { gravityModifier: 1, drag: 0.4 }, blending: THREE.NormalBlending, depthWrite: false,
      sizeOverLifetime: [{ time: 0, value: 0.6 }, { time: 0.3, value: 1 }, { time: 1, value: 0.4 }],
      alphaOverLifetime: [{ time: 0, value: 0.9 }, { time: 0.6, value: 0.7 }, { time: 1, value: 0 }],
    };
    try {
      const em = app.renderer.isWebGPU ? new GpuParticleEmitter(app.renderer as never, cfg as never) : new CpuParticleAdapter(cfg as never);
      em.mesh.frustumCulled = false; em.mesh.renderOrder = 5;
      const mat = em.mesh.material as THREE.Material & { premultipliedAlpha?: boolean };
      mat.premultipliedAlpha = true; // the weather's lesson: premultiplied rgb, blended premultiplied
      try { applySoftParticles(mat as never, { distance: 0.6 } as never); } catch { /* the modifier is optional */ }
      this.scene.add(em.mesh);
      this.splashEmitter = em;
      const fx = waterFx();
      fx.splash = (x, y, z, count) => {
        // FRAME LAW: GPU kernels spawn in RENDER space, the CPU adapter in ABSOLUTE.
        const sp = em.backend === 'gpu' ? this.scene.position : this._splashZero;
        this._splashPos.set(x + sp.x, y + sp.y, z + sp.z);
        em.worldMatrix.setPosition(this._splashPos);
        em.emit(Math.max(1, Math.min(200, Math.round(count))));
      };
    } catch { this.splashEmitter = null; }
  }
  private readonly _splashZero = new THREE.Vector3(0, 0, 0);

  /** Restore the atmosphere fog when surfacing / leaving the Commons. */
  private leaveWater(): void {
    if (this.submerged) {
      setUnderwaterFog(null);
      this.submerged = false;
    }
    atlas.submerged = false;
    if (this.overlay) this.overlay.mesh.visible = false;
    if (this.shafts) { this.shafts.mesh.visible = false; stage4Stats.shaftsVisible = false; }
  }

  private disposeWater(): void {
    this.leaveWater();
    this.lastProbe = null;
    // A rebuild (seed change) used to create new foam / caustics / shafts over
    // the old ones — only dispose() released them (2026-09-12).
    if (this.foam) { this.foam.dispose(); this.foam = null; }
    if (this.caustics) { this.caustics.dispose(); this.caustics = null; }
    if (this.shafts) { this.scene.remove(this.shafts.mesh); disposeLater(this.shafts.mesh.geometry); disposeLater(this.shafts.mesh.material as THREE.Material); this.shafts = null; stage4Stats.shaftsVisible = false; }
    // A stall-tripped worker stayed "broken" for the session: every later
    // build() was pinned to sliced main-thread bakes. A rebuild gets a fresh one.
    this.workerBroken = false;
    if (this.mesh) {
      this.scene.remove(this.mesh);
      disposeLater(this.mesh.geometry);
      disposeLater(this.mesh.material as THREE.Material);
      this.mesh = null;
    }
    if (this.lakeMesh) {
      this.scene.remove(this.lakeMesh);
      disposeLater(this.lakeMesh.geometry);
      disposeLater(this.lakeMesh.material as THREE.Material);
      this.lakeMesh = null;
    }
    // Deferred like the meshes above (warp-teardown law): the lake depth
    // texture, the sea normal map, the overlay's mesh and the shoreline field
    // texture were all bound in the frame in flight when a seed / world swap
    // tore the water down — synchronous dispose() is the "used in submit while
    // destroyed" class.
    disposeLater(this.lakeDepthTex);
    this.lakeDepthTex = null;
    this.lakeHandles = null;
    this.lakeRegion = null;
    this.lakeBake = null;
    if (this.overlay) {
      this.scene.remove(this.overlay.mesh);
      disposeLater(this.overlay);
      this.overlay = null;
    }
    this.bakeWorker?.terminate();
    this.bakeWorker = null;
    this.pendingShoreId = 0;
    this.pendingShoreAt = null;
    this.pendingLakeId = 0;
    disposeLater(this.shoreline);
    this.shoreline = null;
    disposeLater(this.normalMap);
    this.normalMap = null;
    this.handles = null;
    if (this.splashEmitter) { this.scene.remove(this.splashEmitter.mesh); disposeLater(this.splashEmitter.mesh.geometry); disposeLater(this.splashEmitter.mesh.material as THREE.Material); this.splashEmitter = null; }
    const fx = waterFx();
    if (fx.provider) fx.provider = null;
    fx.splash = null;
  }

  dispose(): void {
    this.disposeWater(); // foam, caustics and the shafts go with the water
  }
}
